'use client';

import { c as _c } from "react/compiler-runtime";
import { z } from '@repo/schema';
import { GetPackagingTypeSchema } from '@repo/schema/load';
import { TEST_IDS } from '@repo/sdk/constants';
import { useDotEnv } from '@repo/dot-env/client';
import { useFormContext, useWatch } from '@repo/sdk/form';
import { Button, ButtonGroup, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components';
import { Minus, Plus } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { usePackagingTypes } from './use-packaging-types.ts';
const palletsInputParser = (value: null | number | string | undefined) => {
  const parsedNumber = z.coerce.number().parse(value ?? 0);
  return isNaN(parsedNumber) || parsedNumber < 0 ? 0 : parsedNumber;
};
interface PalletsInputProps {
  autoFocus?: boolean;
  label?: boolean;
  onBlur?: () => void;
  onTypeChange?: (type: undefined | z.infer<typeof GetPackagingTypeSchema>) => void;
}

// Global function to focus pallets input
let globalFocusPalletsInput: (() => void) | null = null;
export const focusPalletsInputGlobally = () => {
  globalFocusPalletsInput?.();
};
export const PalletsInput = forwardRef<HTMLInputElement, PalletsInputProps>((t0, ref) => {
  const $ = _c(21);
  const {
    autoFocus,
    label: t1,
    onBlur,
    onTypeChange
  } = t0;
  const label = t1 === undefined ? true : t1;
  const blurTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const {
    control
  } = useFormContext();
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => inputRef.current;
    t3 = [inputRef];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useImperativeHandle(ref, t2, t3);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      globalFocusPalletsInput = () => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      };
      return _temp;
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] !== onBlur) {
    t6 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        const container = document.activeElement?.closest("[data-packaging-container]");
        const isInputFocused = document.activeElement?.matches("input[type=\"number\"]");
        if (!container && !isInputFocused) {
          onBlur?.();
        }
      }, 150);
    };
    $[4] = onBlur;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handleBlurWithDelay = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const handleFocus = t7;
  let t8;
  if ($[7] !== control) {
    t8 = {
      control,
      name: "packaging_type_id"
    };
    $[7] = control;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const currentPackagingTypeId = useWatch(t8);
  const {
    data: packagingTypes,
    isPending
  } = usePackagingTypes();
  let t9;
  if ($[9] !== currentPackagingTypeId || $[10] !== packagingTypes) {
    t9 = packagingTypes?.find(item => item.id === Number(currentPackagingTypeId));
    $[9] = currentPackagingTypeId;
    $[10] = packagingTypes;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const currentPackagingType = t9;
  const showNumericInput = !isPending && currentPackagingType?.title !== "Other";
  let t10;
  if ($[12] !== autoFocus || $[13] !== handleBlurWithDelay || $[14] !== label || $[15] !== onTypeChange || $[16] !== showNumericInput) {
    t10 = t11 => {
      const {
        field
      } = t11;
      const parsedValue = palletsInputParser(field.value);
      const handlePlus = () => {
        field.onChange((parsedValue ?? 0) + 1);
      };
      const handleMinus = () => {
        if (parsedValue > 0) {
          field.onChange(parsedValue - 1);
        }
      };
      const handleInputChange = event => {
        const inputValue = event.target.value;
        const numberValue = Number(inputValue);
        const newValue = isNaN(numberValue) || numberValue < 0 ? 0 : numberValue;
        field.onChange(newValue);
      };
      return <FormItem>{label && <FormLabel className="text-card-foreground">Pallet quantity (approx.)</FormLabel>}<FormControl><ButtonGroup data-packaging-container={true} onBlur={handleBlurWithDelay} onFocus={handleFocus}>{showNumericInput && <><Button data-testid={TEST_IDS.cargo.packaging.minusButton} onClick={handleMinus} size="icon" type="button" variant="outline"><Minus /></Button><Input autoFocus={autoFocus} className={cn("min-w-16 rounded-none text-center", "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none")} data-testid={TEST_IDS.cargo.packaging.input} min={0} name={field.name} onChange={handleInputChange} placeholder="0" ref={inputRef} type="number" value={parsedValue.toString()} /><Button onClick={handlePlus} size="icon" type="button" variant="outline"><Plus /></Button></>}<PackagingTypeSelect isOther={!showNumericInput} onTypeChange={onTypeChange} /></ButtonGroup></FormControl><FormMessage /></FormItem>;
    };
    $[12] = autoFocus;
    $[13] = handleBlurWithDelay;
    $[14] = label;
    $[15] = onTypeChange;
    $[16] = showNumericInput;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== control || $[19] !== t10) {
    t11 = <FormField control={control} name="pallets" render={t10} />;
    $[18] = control;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
});
PalletsInput.displayName = 'PalletsInput';
interface PackagingTypeSelectProps {
  isOther?: boolean;
  onTypeChange?: (type: undefined | z.infer<typeof GetPackagingTypeSchema>) => void;
}
export const PackagingTypeSelect = t0 => {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isOther: t2,
    onTypeChange
  } = t1;
  const isOther = t2 === undefined ? false : t2;
  const {
    control,
    setValue
  } = useFormContext();
  const env = useDotEnv();
  const {
    data: packagingTypes
  } = usePackagingTypes();
  let t3;
  if ($[2] !== control) {
    t3 = {
      control,
      name: "packaging_type_id"
    };
    $[2] = control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const currentPackagingTypeId = useWatch(t3);
  let t4;
  if ($[4] !== env || $[5] !== packagingTypes) {
    t4 = packagingTypes?.find(item => env.SOURCE?.includes("kcharles") ? item.title.includes("UK") : item.title.includes("EU") || item.title.includes("UK"))?.id;
    $[4] = env;
    $[5] = packagingTypes;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const defaultValue = t4;
  let t5;
  let t6;
  if ($[7] !== currentPackagingTypeId || $[8] !== defaultValue || $[9] !== setValue) {
    t5 = () => {
      if (defaultValue && !currentPackagingTypeId) {
        setValue("packaging_type_id", defaultValue);
      }
    };
    t6 = [defaultValue, currentPackagingTypeId, setValue];
    $[7] = currentPackagingTypeId;
    $[8] = defaultValue;
    $[9] = setValue;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[12] !== currentPackagingTypeId || $[13] !== onTypeChange || $[14] !== packagingTypes) {
    t7 = () => {
      if (packagingTypes && onTypeChange && currentPackagingTypeId) {
        const currentType = packagingTypes.find(item_0 => item_0.id === Number(currentPackagingTypeId));
        onTypeChange(currentType);
      }
    };
    t8 = [packagingTypes, onTypeChange, currentPackagingTypeId];
    $[12] = currentPackagingTypeId;
    $[13] = onTypeChange;
    $[14] = packagingTypes;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== isOther || $[18] !== onTypeChange || $[19] !== packagingTypes) {
    t9 = t10 => {
      const {
        field
      } = t10;
      return <FormItem className="w-full space-y-0"><FormControl><Select items={packagingTypes?.map(_temp2) ?? []} onValueChange={value => {
            field.onChange(value);
            if (packagingTypes && onTypeChange) {
              const selectedType = packagingTypes.find(item_2 => item_2.id === Number(value));
              onTypeChange(selectedType);
            }
          }} value={field.value?.toString()}><SelectTrigger className={cn(!isOther && "rounded-l-none")} data-testid={TEST_IDS.cargo.packaging.typeSelect}><SelectValue /></SelectTrigger><SelectContent>{packagingTypes?.map(_temp3)}</SelectContent></Select></FormControl><FormMessage /></FormItem>;
    };
    $[17] = isOther;
    $[18] = onTypeChange;
    $[19] = packagingTypes;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== control || $[22] !== t9) {
    t10 = <FormField control={control} name="packaging_type_id" render={t9} data-sentry-element="FormField" data-sentry-component="PackagingTypeSelect" data-sentry-source-file="input.tsx" />;
    $[21] = control;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
function _temp() {
  globalFocusPalletsInput = null;
}
function _temp2(item_1) {
  return {
    label: item_1.title,
    value: item_1.id.toString()
  };
}
function _temp3(item_3) {
  return <SelectItem key={item_3.id} value={item_3.id.toString()}>{item_3.title}</SelectItem>;
}